'use client';

import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';
import {
  Button,
  ConfirmationDialog,
  EntityResolverProvider,
  AddToEvidenceProvider,
  ArtifactsProvider,
  BodyDefault,
  HeadingItem,
  IconButton,
  Skeleton,
  TraceView,
  toasterApi,
  Tooltip,
  TraceSimplifiedView,
  mapTraceMetadataFromSmokeTestHistory,
  type TraceViewHandle,
  type TraceExpandedSections,
  type TraceSimplifiedViewHandle,
  CopyIdButton,
} from '@terra/ui-design-system';
import { useMentions } from '../../hooks/useMentions';
import { useChatStore } from '../../store/chatStore';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { Har } from 'har-format';
import {
  CheckListIcon,
  Download01Icon,
  FileScriptIcon,
  Message01Icon,
  PlayIcon,
  Route01Icon,
  StopIcon,
  Delete02Icon,
} from '@terra/ui-icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useTraceEntityResolvers } from '../../hooks/useTraceEntityResolvers';
import { useMainContentTabs } from '../../store/layoutStore';
import { useTraceScrollPreservation } from '../../store/tabViewStateStore';
import { useTraceViewSettingsStore } from '../../store/traceViewSettingsStore';
import { useEvidenceMutations } from '../../hooks/api/useAddEvidence';
import { ExecutionTraceTabs } from '../expected-response-observations/ExecutionTraceTabs';
import { buildCreateFindingChatPrompt } from './build-create-finding-chat-prompt';

type AgentExecutionTraceContentProps = {
  applicationId: string;
  executionId: string;
  threadId: string | undefined;
  title: string;
  history: SmokeTestHistoryResponseDto | undefined;
  isLoading: boolean;
  error: Error | null;
  expandedSections?: TraceExpandedSections;
  onExpandedSectionsChange?: (sections: TraceExpandedSections) => void;
  executionTab?: string;
  onExecutionTabChange?: (tab: string) => void;
  onAbortSuccess?: () => Promise<void> | void;
  onResumeSuccess?: () => Promise<void> | void;
  onDeleteSuccess?: () => Promise<void> | void;
};

export function AgentExecutionTraceContent({
  applicationId,
  executionId,
  threadId,
  title: executionTitle,
  history,
  isLoading,
  error,
  expandedSections,
  onExpandedSectionsChange,
  executionTab: executionTabProp,
  onExecutionTabChange,
  onAbortSuccess,
  onResumeSuccess,
  onDeleteSuccess,
}: AgentExecutionTraceContentProps) {
  const [isExportingHars, setIsExportingHars] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localExecutionTab, setLocalExecutionTab] = useState('overview');
  const executionTab = executionTabProp ?? localExecutionTab;
  const setExecutionTab = onExecutionTabChange ?? setLocalExecutionTab;
  const showDetailedView = useTraceViewSettingsStore(
    (state) => state.showDetailedView,
  );
  const setShowDetailedView = useTraceViewSettingsStore(
    (state) => state.setShowDetailedView,
  );
  const t = useTranslations('traceContent');
  const tExecutions = useTranslations('designSystem.executions');
  const { addMention } = useMentions();
  const appendTextToInput = useChatStore((state) => state.appendTextToInput);
  const { closeTab } = useMainContentTabs();
  const simplifiedViewRef = useRef<TraceSimplifiedViewHandle>(null);
  const detailedViewRef = useRef<TraceViewHandle>(null);
  const {
    addEvidence: { mutateAsync: addEvidence },
    updateEvidence: { mutateAsync: updateEvidence },
    removeEvidence: { mutateAsync: removeEvidence },
  } = useEvidenceMutations({
    executionId,
  });
  const traceMetadata = useMemo(
    () => mapTraceMetadataFromSmokeTestHistory(history?.metadata),
    [history?.metadata],
  );
  const hasSignal = Boolean(history?.signal?.id);
  const evidenceArtifactIds = useMemo(() => {
    return new Set(
      (traceMetadata?.evidences ?? [])
        .map((evidence) => evidence.artifactId)
        .filter((artifactId): artifactId is string => Boolean(artifactId)),
    );
  }, [traceMetadata?.evidences]);
  const handleAddToEvidence = useCallback(
    async (artifactId: string, description: string): Promise<void> => {
      await addEvidence({ artifactId, description });
    },
    [addEvidence],
  );
  const handleUpdateEvidence = useCallback(
    async (artifactId: string, description: string): Promise<void> => {
      await updateEvidence({ artifactId, description });
    },
    [updateEvidence],
  );
  const handleRemoveEvidence = useCallback(
    async (artifactId: string): Promise<void> => {
      await removeEvidence({ artifactId });
    },
    [removeEvidence],
  );

  const isOverviewTabActive = executionTab === 'overview';

  useTraceScrollPreservation({
    threadId: executionId,
    isLoading,
    isActive: isOverviewTabActive && !showDetailedView,
    viewRef: simplifiedViewRef,
    stateKey: 'scrollPosition',
  });
  useTraceScrollPreservation({
    threadId: executionId,
    isLoading,
    isActive: isOverviewTabActive && showDetailedView,
    viewRef: detailedViewRef,
    stateKey: 'detailedScrollPosition',
  });
  const executionStatus = useMemo(():
    | 'running'
    | 'completed'
    | 'error'
    | 'aborted' => {
    if (history?.status) {
      switch (history.status) {
        case 'RUNNING':
          return 'running';
        case 'COMPLETED':
          return 'completed';
        case 'ABORTED':
        case 'PAUSED':
          return 'aborted';
        case 'FAILED':
          return 'error';
        default:
          break;
      }
    }

    if (history?.plan?.some((p) => p.status === 'in_progress')) {
      return 'running';
    }

    return 'completed';
  }, [history?.status, history?.plan]);
  const canAbort = useMemo(() => {
    if (!history?.status) return false;
    const terminalStates = ['COMPLETED', 'FAILED', 'ABORTED', 'PAUSED'];

    return !terminalStates.includes(history.status);
  }, [history?.status]);
  const canResume = useMemo(() => {
    return history?.status === 'PAUSED';
  }, [history?.status]);
  const isAttackSkipped = Boolean(traceMetadata?.skipAttackReason);
  const handleNavigateToToolCall = useCallback(() => {
    // The audit log links reference tool calls surfaced in the execution
    // overview. Switch back to the detailed overview so the referenced tool
    // call is visible. Precise scroll-to-tool-call is not available on the
    // current TraceViewHandle, so we only restore the detailed overview view.
    setShowDetailedView(true);
    setExecutionTab('overview');
  }, [setShowDetailedView, setExecutionTab]);
  const entityResolvers = useTraceEntityResolvers(applicationId);
  const handleExport = useCallback(() => {
    if (!history) return;
    const exportData = {
      executionId,
      history,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `agent-execution-${executionId}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [executionId, history]);
  const handleExportAllHars = useCallback(async () => {
    if (!threadId) return;
    setIsExportingHars(true);

    try {
      const response =
        await terraProxyApiClient.getToolHarsByThreadId(threadId);
      const hars = response?.hars ?? [];

      if (hars.length === 0) {
        console.warn('No HAR data found for this execution');

        return;
      }

      const harExport: Har = {
        log: {
          version: '1.2',
          creator: {
            name: 'Terra Security Platform',
            version: '1.0',
          },
          pages: [],
          entries: hars.map((har) => ({
            ...har.harEntry,
            cache: har.harEntry.cache ?? {},
            timings: {
              send: 0,
              ...har.harEntry.timings,
            },
            _toolCallId: har.toolCallId,
            _toolName: har.toolName,
            _toolType: har.toolType,
          })),
        },
      };
      const blob = new Blob([JSON.stringify(harExport, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `agent-execution-hars-${executionId}-${Date.now()}.har`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error('Failed to export HAR data:', exportError);
    } finally {
      setIsExportingHars(false);
    }
  }, [threadId, executionId]);
  const buildTraceMention = useCallback(() => {
    const label = executionTitle
      ? `Execution: ${executionTitle.slice(0, 50)}${executionTitle.length > 50 ? '...' : ''}`
      : `Execution: ${executionId.slice(0, 20)}...`;
    const traceThreadId = threadId ?? executionId;

    return {
      id: `agent-execution-${executionId}`,
      label,
      icon: <Route01Icon size={12} />,
      type: 'child' as const,
      mentionEntity: {
        entityType: 'Trace' as const,
        entityId: traceThreadId,
        label,
        extraData: {
          executionId,
        },
      },
    };
  }, [executionId, executionTitle, threadId]);
  const handleInvestigateInChat = useCallback(() => {
    const mention = buildTraceMention();

    setTimeout(() => {
      appendTextToInput(t('investigateChatPrompt'));
      addMention(mention);
    }, 0);
  }, [buildTraceMention, addMention, appendTextToInput, t]);
  const handleCreateFindingInChat = useCallback(() => {
    const mention = buildTraceMention();
    const traceThreadId = threadId ?? executionId;

    setTimeout(() => {
      appendTextToInput(
        buildCreateFindingChatPrompt({
          leadIn: t('createFindingChatPrompt'),
          instruction: t('createFindingChatIdsInstruction'),
          traceThreadId,
          postAttackTraceId: history?.postAttackTraceId,
          signalId: history?.signal?.id ?? history?.signalId,
        }),
      );
      addMention(mention);
    }, 0);
  }, [
    buildTraceMention,
    addMention,
    appendTextToInput,
    t,
    threadId,
    executionId,
    history,
  ]);
  const handleAbort = useCallback(async () => {
    if (isAborting) return;
    setIsAborting(true);

    try {
      await agentExecutionClient.abort({
        params: { executionId },
        body: {},
      });
      await onAbortSuccess?.();
    } catch (abortError) {
      console.error('Error aborting execution:', abortError);
    } finally {
      setIsAborting(false);
    }
  }, [executionId, isAborting, onAbortSuccess]);
  const handleResume = useCallback(async () => {
    if (isResuming) return;
    setIsResuming(true);

    try {
      await agentExecutionClient.resume({
        params: { executionId },
        body: {},
      });
      await onResumeSuccess?.();
      toasterApi.success({
        title: 'Execution resumed',
        description: 'The execution is resuming.',
      });
    } catch (resumeError) {
      console.error('Failed to resume execution:', resumeError);
      toasterApi.error({
        title: 'Failed to resume',
        description: 'There was an error resuming the execution.',
      });
    } finally {
      setIsResuming(false);
    }
  }, [executionId, isResuming, onResumeSuccess]);
  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      await agentExecutionClient.delete({
        params: { executionId },
        body: {},
      });
      await onDeleteSuccess?.();
      toasterApi.success({
        title: 'Execution deleted',
        description: 'The execution has been deleted successfully.',
      });
      setIsDeleteDialogOpen(false);
      closeTab(`agent-execution-${executionId}`);
    } catch (deleteError) {
      console.error('Failed to delete execution:', deleteError);
      toasterApi.error({
        title: 'Failed to delete execution',
        description: 'There was an error deleting the execution.',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [closeTab, executionId, onDeleteSuccess]);

  if (isLoading) {
    return (
      <Box h="full" w="full" p="6">
        <VStack gap="4" alignItems="flex-start" w="full">
          <Skeleton h={8} w={50} />
          <Skeleton h={6} w={75} />
          <Skeleton h={100} w="full" />
        </VStack>
      </Box>
    );
  }

  if (error || !history) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4" textAlign="center">
          <HeadingItem>Execution Not Found</HeadingItem>
          <BodyDefault color="fg.muted">
            {error?.message || 'The execution could not be loaded.'}
          </BodyDefault>
        </VStack>
      </Box>
    );
  }

  const title = executionTitle || 'Agent Execution';

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        {/* Header */}
        <Box
          px="4"
          py="3"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.default"
        >
          <HStack gap="3" alignItems="center" w="full">
            <HStack gap="2" flex="1" minW={0} alignItems="center">
              <HeadingItem truncate title={title}>
                {title}
              </HeadingItem>
            </HStack>

            {/* Action Buttons */}
            {executionId && (
              <CopyIdButton
                id={executionId}
                entityName={tExecutions('execution')}
              />
            )}
            <Tooltip title="Export Execution">
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleExport}
                aria-label="Export execution"
              >
                <Download01Icon size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                isExportingHars
                  ? 'Exporting...'
                  : 'Export All HTTP Requests (HAR)'
              }
            >
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleExportAllHars}
                aria-label="Export all HARs"
                disabled={isExportingHars || !threadId}
              >
                <FileScriptIcon size={14} />
              </IconButton>
            </Tooltip>

            {/* Abort Button */}
            {canAbort && (
              <Tooltip title="Abort execution">
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={handleAbort}
                  aria-label="Abort execution"
                  disabled={isAborting}
                >
                  <StopIcon size={14} />
                </IconButton>
              </Tooltip>
            )}

            {/* Resume Button */}
            {canResume && (
              <Tooltip title="Resume execution">
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={handleResume}
                  aria-label="Resume execution"
                  disabled={isResuming}
                >
                  <PlayIcon size={14} />
                </IconButton>
              </Tooltip>
            )}

            {/* Delete Button */}
            <Tooltip title="Delete execution">
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleDeleteClick}
                aria-label="Delete execution"
              >
                <Delete02Icon size={14} />
              </IconButton>
            </Tooltip>

            {/* Investigate in Chat Button */}
            <Button size="xs" variant="ghost" onClick={handleInvestigateInChat}>
              <Message01Icon size={12} />
              Investigate
            </Button>

            {/* Generate Finding in Chat Button */}
            <Button
              size="xs"
              variant="ghost"
              onClick={handleCreateFindingInChat}
              aria-label="Generate finding on this execution"
            >
              <CheckListIcon size={12} />
              Generate Finding
            </Button>
          </HStack>
        </Box>

        {/* Content */}
        <Box flex="1" overflow="hidden">
          <AddToEvidenceProvider
            onAddToEvidence={handleAddToEvidence}
            onUpdateEvidence={handleUpdateEvidence}
            onRemoveEvidence={handleRemoveEvidence}
          >
            <ArtifactsProvider
              artifacts={history.artifacts ?? []}
              evidenceArtifactIds={evidenceArtifactIds}
            >
              <ExecutionTraceTabs
                applicationId={applicationId}
                threadId={threadId}
                executionStatus={executionStatus}
                isAttackSkipped={isAttackSkipped}
                activeTab={executionTab}
                onActiveTabChange={setExecutionTab}
                onNavigateToToolCall={handleNavigateToToolCall}
                overview={
                  <EntityResolverProvider resolvers={entityResolvers}>
                    {showDetailedView ? (
                      <Box h="full" overflow="hidden">
                        <TraceView
                          ref={detailedViewRef}
                          history={history}
                          maxHeight="100%"
                          status={executionStatus}
                        />
                      </Box>
                    ) : (
                      <Box h="full" overflow="hidden">
                        <TraceSimplifiedView
                          ref={simplifiedViewRef}
                          signal={
                            hasSignal && history.signal
                              ? {
                                  id: history.signal.id,
                                  description:
                                    history.signal.description ?? undefined,
                                  category:
                                    history.signal.category ?? undefined,
                                  analysis:
                                    history.signal.analysis ?? undefined,
                                  stage: history.signal.stage ?? undefined,
                                  status: history.signal.status ?? undefined,
                                }
                              : undefined
                          }
                          plan={history.plan}
                          metadata={traceMetadata}
                          status={executionStatus}
                          maxHeight="100%"
                          expandedSections={expandedSections}
                          onExpandedSectionsChange={onExpandedSectionsChange}
                        />
                      </Box>
                    )}
                  </EntityResolverProvider>
                }
              />
            </ArtifactsProvider>
          </AddToEvidenceProvider>
        </Box>
      </VStack>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={({ open }) => setIsDeleteDialogOpen(open)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        variant="destructive"
        title="Delete Execution"
        description="Are you sure you want to delete this execution? This will permanently delete all execution data including messages and files. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
}
